import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileText, X, Loader2 } from "lucide-react";

const ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX_BYTES = 5 * 1024 * 1024;

export default function ResumeUploader({ value, onChange, testId = "resume-uploader", error }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      onChange(null, "File must be 5MB or less");
      return;
    }
    const okType = /pdf|msword|officedocument\.wordprocessingml/.test(f.type);
    if (!okType) {
      onChange(null, "Only PDF, DOC or DOCX accepted");
      return;
    }
    onChange(f, null);
  };

  return (
    <div className="space-y-2">
      <label
        data-testid={testId}
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed cursor-pointer transition-colors px-6 py-8 ${
          drag ? "border-primary bg-primary/5" : value ? "border-slate-300 bg-white" : "border-slate-300 bg-slate-50 hover:bg-white hover:border-primary/40"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />
        {value ? (
          <div className="flex items-center gap-3 w-full">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-primary" strokeWidth={1.6} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{value.name}</div>
              <div className="text-xs text-slate-500">{(value.size / 1024).toFixed(0)} KB · {value.type.split("/").pop().toUpperCase()}</div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onChange(null, null); if (inputRef.current) inputRef.current.value = ""; }}
              data-testid={`${testId}-remove`}
              className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-xl bg-primary/10 flex items-center justify-center">
              <UploadCloud className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">Upload your resume</p>
            <p className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX · Max 5 MB</p>
            <p className="text-xs text-primary mt-3 font-medium">Click or drag & drop</p>
          </div>
        )}
      </label>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function UploadingOverlay({ active, label = "Submitting…" }) {
  if (!active) return null;
  return (
    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm rounded-3xl flex items-center justify-center z-10">
      <div className="inline-flex items-center gap-2 text-primary font-semibold">
        <Loader2 className="w-5 h-5 animate-spin" />
        {label}
      </div>
    </div>
  );
}
